import * as Sentry from '@sentry/react'

import { AnalyticsEvent, logAnalyticsEvent, runProvider } from 'utils/analytics'
import { logger } from 'utils/logging'
import { Coordinates, KeyPickupDTO, MeetingOnSiteDTO, OrganizationThirdPartyDTO, PlaceOrderBody, ProductListItemDTO } from 'models/purchaseFlow'
import { EditingCategory, ProductKind, ProductSegment } from 'constants/product'
import { StagingConfiguration, StagingRequestDTO } from 'models/virtualStaging'
import { useCallback, useMemo } from 'react'
import { usePFInvoicingAddresses, useProductAndInstructionLists, usePurchaseFlowConfig, usePurchaseFlowOrderMeta, usePurchaseFlowProducts, usePurchaseFlowRealEstateProperty, useTargetOrderUser } from 'components/pages/PurchaseFlow/_main/contexts'

import { APIRequestErrorType } from 'constants/API'
import { Moment } from 'moment'
import { RealEstatePropertyPurchaseFlowDTO } from 'models/property'
import { Userpilot } from 'userpilot'
import { bigFromDecimal } from 'utils/price'
import constate from 'constate'
import { getAPIRoomItemFromStagingRoom } from 'utils/serialization/getAPIRoomItemFromStagingRoom'
import { getCityFromGooglePlace } from 'utils/location'
import { isEditingCategory } from 'utils/validators'
import { missingFileNames, parseMissingSessionFiles } from 'models/purchaseFlow/missingSessionFiles'
import { useAddressMap } from 'components/common/AdressMap'
import { useAuth0 } from 'utils/auth'
import { useBookCreative } from 'components/pages/PurchaseFlow/common'
import { useConsumptionCertificateStep } from '../../../ConsumptionCertificateStep'
import { useEmbedConsent } from 'components/pages/PurchaseFlow/_main/contexts/EmbedConsent.context'
import { useEnergyCertificateStep } from '../../../EnergyCertificateStep'
import { useFloorPlanCertification } from '../../../FloorPlanCertification/_main/FloorPlanCertificationStep.context'
import { useFloorPlanConfig } from '../../../FloorPlanConfig/_main/FloorPlanConfigStep.context'
import { useMeasurements } from '../../../MeasurementStep/MeasurementStep.context'
import { usePlaceOrder } from 'dataQueries/purchase.query'
import { usePropertyInformationStep } from '../../../PropertyInformationStep/PropertyInformationStep.context'
import { usePurchaseFlowPaymentStatus } from './PurchaseFlowPaymentStatus.context'
import { useRoomAPI } from 'components/contexts/RoomAPI.context'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

export const [PurchaseFlowPlaceOrderProvider, usePurchaseFlowPlaceOrder] = constate(() => {
  const { t } = useTranslation(['order', 'category', 'product', 'embed'])
  const { roles } = useAuth0()
  const { ensureConsentRecorded } = useEmbedConsent()
  const { adminSelectedUserEmail, orderWorkspaceId } = useTargetOrderUser()
  const { selectedCreative } = useBookCreative()
  const { spawnErrorToast, spawnSuccessToast } = useSnackbar()
  const { getFloorPlanRequestData } = useFloorPlanConfig()
  const { getFloorPlanCertificationRequestData } = useFloorPlanCertification()
  const { getAuthoritiesDocumentRequestData } = usePropertyInformationStep()
  const { getMeasurementRequestData } = useMeasurements()
  const { getConsumptionCertificateRequestData } = useConsumptionCertificateStep()
  const { getEnergyCertificateRequestData } = useEnergyCertificateStep()
  const { getAllRooms, allRoomsCompleted } = useRoomAPI()
  const { markerPosition } = useAddressMap()

  const {
    selectedAssignmentPlace,
    realEstateProperty,
    realEstatePropertyId,
    addressManualValue,
    isAddressManually,
  } = usePurchaseFlowRealEstateProperty()

  const {
    selectedSegment,
    hasDocumentProduct,
    hasFloorPlanProduct,
    hasFloorPlanCertificationProduct,
    hasMeasurementProduct,
    hasConsumptionCertificateProduct,
    hasEnergyCertificateProduct,
    isSkyReplacementEnabled,
    selectedSkyReplacement,
  } = usePurchaseFlowProducts()

  const {
    getProductQuantityList,
    getProductFullList,
    getInstructionFullList,
    getInstructionQuantityList,
  } = useProductAndInstructionLists()

  const {
    sessionId,
    orderAttemptId,
    launchedFrom,
    selectedCategory,
    organizationInstructions,
    selectedCountryCode,
    catalogueCurrency,
    catalogueDiscount,
    catalogueVat,
    catalogueTimezone,
    externalReportingInstruction,
    enableSkipCtAutomation,
    enableSkipExternalProviderSending,
  } = usePurchaseFlowConfig()

  const {
    reference,
    shootingComments,
    meetingInformation,
    ccEmails,
    propertyOwner,
    selectedMeetingType,
    selectedExtraServices,
    isStripePaymentMethod,
    externalReportingField,
    selectedKeyPickupPlace,
    keyPickupTravelCostProduct,
  } = usePurchaseFlowOrderMeta()

  const { selectedInvoicingAddress, recalculatePricesMutation } = usePFInvoicingAddresses()

  const {
    isStripeProcessing,
    isStripePaymentAccepted,
    isReceiptEmailValid,
    selectedSavedPaymentMethod,
    stripeInputElementsAreValid,
    setIsStripeConfirmed,
    setStripeError,
    setIsStripeProcessing
  } = usePurchaseFlowPaymentStatus()

  const placeOrderMutation = usePlaceOrder()


  /** Serializes staging configuration information into payload */
  const getStagingConfig = useCallback((): StagingRequestDTO | undefined => {
    // state guard
    if (selectedCategory !== EditingCategory.STAGING || !allRoomsCompleted) return undefined

    const stagedRooms = getAllRooms()

    // Reduce room objects to Records of config items indexed by image id
    const roomDetails = stagedRooms.reduce((stagingMap, room) => {

      const roomConfig = getAPIRoomItemFromStagingRoom(room)

      const imageStyleMap: StagingConfiguration = room.images.reduce((imageMap, { id }) => ({
        ...imageMap,
        [id]: roomConfig
      }), {})

      return {
        ...stagingMap,
        ...imageStyleMap,
      }
    }, {})

    return {
      details: roomDetails
    }

  }, [allRoomsCompleted, getAllRooms, selectedCategory])

  const orderIsBeingPlaced = useMemo(() => placeOrderMutation.isPending, [placeOrderMutation.isPending])
  const orderHasBeenPlaced = useMemo(() => placeOrderMutation.isSuccess, [placeOrderMutation.isSuccess])
  // A card / SEPA order is placed before its payment is confirmed, so placement alone does not finish it.
  const orderIsComplete = useMemo(
    () => orderHasBeenPlaced && (!isStripePaymentMethod || isStripePaymentAccepted),
    [isStripePaymentAccepted, isStripePaymentMethod, orderHasBeenPlaced]
  )
  const orderPlacementHasError = useMemo(() => placeOrderMutation.isError, [placeOrderMutation.isError])
  const orderPlacementForbiddenErrorMessage = useMemo(() => {
    if (!placeOrderMutation.isError) return undefined

    if (placeOrderMutation.error.code === APIRequestErrorType.FORBIDDEN_ERROR) return (placeOrderMutation.error.response?.data as any)?.message ?? undefined

    return undefined
  }, [placeOrderMutation])

  const vatNumber = useMemo(() => bigFromDecimal(catalogueVat).toNumber(), [catalogueVat])
  const discountNumber = useMemo(() => bigFromDecimal(catalogueDiscount).toNumber(), [catalogueDiscount])

  const isOrderOrStripeInProgress = useMemo(() => isStripePaymentMethod ? (orderIsBeingPlaced || isStripeProcessing) : orderIsBeingPlaced, [isStripeProcessing, isStripePaymentMethod, orderIsBeingPlaced])

  // VAT/discount recalculation for the selected invoicing address is in-flight. Placing now
  // could send an address whose VAT validation hasn't resolved, charging the wrong rate
  // (ISSUE-2267).
  const isRecalculatingPrices = useMemo(() => recalculatePricesMutation.isPending, [recalculatePricesMutation.isPending])

  const isPlaceOrderButtonDisabled = useMemo(() => {
    // Submit is already processing
    if (isOrderOrStripeInProgress) return true

    // Invoicing-address VAT recalculation is still in-flight
    if (isRecalculatingPrices) return true

    // Case of stripe not being used
    if (!isStripePaymentMethod) return false

    // Stripe is being used
    // if (selectedSavedPaymentMethod === null) return true
    if (!isReceiptEmailValid) return true
    if (selectedSavedPaymentMethod === null && !stripeInputElementsAreValid) return true

    return false
  }, [isOrderOrStripeInProgress, isRecalculatingPrices, isReceiptEmailValid, isStripePaymentMethod, selectedSavedPaymentMethod, stripeInputElementsAreValid])

  const formatOrderDateToString = useCallback((startDate: Moment) => {
    if (!catalogueTimezone) return ''
    const dateTime = startDate.utc(true).tz(catalogueTimezone, true)

    return dateTime.toISOString()
  }, [catalogueTimezone])

  const getExternalReporting = useCallback(() => {
    if (!externalReportingInstruction) return undefined

    return externalReportingField
  }, [externalReportingField, externalReportingInstruction])

  const getMeetingOnSite = useCallback((): MeetingOnSiteDTO | undefined => {
    if (selectedMeetingType !== ProductKind.MEETING_ON_SITE) return undefined

    const { date, name, phone, comments, email } = meetingInformation[ProductKind.MEETING_ON_SITE]

    if (!date || !catalogueTimezone || !name) return undefined

    return {
      name,
      phone,
      email,
      comment: comments,
      date: formatOrderDateToString(date),
      timezone: catalogueTimezone,
    }
  }, [catalogueTimezone, formatOrderDateToString, meetingInformation, selectedMeetingType])

  const getOrganizationThirdParty = useCallback((): OrganizationThirdPartyDTO | undefined => {
    if (selectedMeetingType !== ProductKind.ORGANIZATION_THIRD_PARTY) return undefined

    const { name, phone, comments, email } = meetingInformation[ProductKind.ORGANIZATION_THIRD_PARTY]

    if (!catalogueTimezone || !name) return undefined

    return {
      name,
      phone,
      email,
      comment: comments,
      timezone: catalogueTimezone,
    }
  }, [catalogueTimezone, meetingInformation, selectedMeetingType])

  const getKeyPickup = useCallback((): KeyPickupDTO | undefined => {
    if (selectedMeetingType !== ProductKind.KEYS_PICKUP) return undefined

    const { name, phone, comments, email, date } = meetingInformation[ProductKind.KEYS_PICKUP]

    if (!date || !selectedKeyPickupPlace || !catalogueTimezone) return undefined

    return {
      name,
      phone,
      email,
      comment: comments,
      date: formatOrderDateToString(date),
      address: selectedKeyPickupPlace.address,
      timezone: catalogueTimezone,
      coordinates: selectedKeyPickupPlace.coordinate,
    }
  }, [catalogueTimezone, formatOrderDateToString, meetingInformation, selectedKeyPickupPlace, selectedMeetingType])

  const handlePlaceOrder = useCallback(() => {
    if (orderIsBeingPlaced || orderHasBeenPlaced) return
    if (!selectedCategory || !sessionId) return
    if (!selectedAssignmentPlace && !isEditingCategory(selectedCategory)) return
    if (!organizationInstructions) return

    if (!selectedInvoicingAddress || selectedInvoicingAddress === 'new') return

    const stagingConfiguration = getStagingConfig()
    if (selectedCategory === EditingCategory.STAGING && !stagingConfiguration) return
    const isFavouriteCTSelected = selectedExtraServices.has(ProductKind.FAVOURITE_CREATIVE)
    if (isFavouriteCTSelected && !selectedCreative) return

    const isKeyPickupSelected = selectedMeetingType === ProductKind.KEYS_PICKUP

    if (isKeyPickupSelected && !keyPickupTravelCostProduct) {
      spawnErrorToast(t('order:step_validation.keys_pickup_travel_cost_unavailable'))
      logger.warn('handlePlaceOrder: keys_pickup travel-cost product missing', undefined, {
        context: {
          component: 'PurchaseFlowPlaceOrder',
          action: 'place_order',
          metadata: { reason: 'keys_pickup_travel_cost_unavailable' },
        },
      })
      return
    }

    const selectedFavouriteCreativeId = isFavouriteCTSelected ? selectedCreative?.id : undefined

    const quantityProductList = [
      ...getProductQuantityList(),
      ...getInstructionQuantityList(),
    ]

    const fullProductList: ProductListItemDTO[] = [
      ...getProductFullList(),
      ...getInstructionFullList(),
    ]

    // Append Favourite CT travel cost product if Favourite CT is selected
    if (isFavouriteCTSelected && selectedCreative) {

      if (selectedCreative.travelCost.productQuantity > 0) {
        const productItem = {
          id: selectedCreative.travelCost.productId,
          count: selectedCreative.travelCost.productQuantity,
          isOption: false,
        }

        quantityProductList.push(productItem)
        fullProductList.push({
          ...productItem,
          name: t(`product:p_${productItem.id}`),
          pricePerItem: selectedCreative.travelCost.productUnitPrice,
        })
      }
    }

    // Append Key pickup travel cost if key pickup is selected and quantity it not 0
    if (isKeyPickupSelected && keyPickupTravelCostProduct) {

      const productItem = {
        id: keyPickupTravelCostProduct.productId,
        count: keyPickupTravelCostProduct.productQuantity || 0,
        isOption: false,
      }

      quantityProductList.push(productItem)
      fullProductList.push({
        ...productItem,
        name: t(`product:p_${productItem.id}`),
        pricePerItem: keyPickupTravelCostProduct.productUnitPrice,
      })
    }

    const floorPlanCertificationData = hasFloorPlanCertificationProduct ? getFloorPlanCertificationRequestData() : undefined
    const floorPlanConfigData = hasFloorPlanProduct ? getFloorPlanRequestData() : undefined

    const authoritiesDocumentData = hasDocumentProduct ? getAuthoritiesDocumentRequestData() : undefined

    const measurementRequestData = hasMeasurementProduct ? getMeasurementRequestData() : undefined

    const consumptionCertificateRequestData = hasConsumptionCertificateProduct ? getConsumptionCertificateRequestData() : undefined
    const energyCertificateRequestData = hasEnergyCertificateProduct ? getEnergyCertificateRequestData() : undefined

    let placeObject: {
      countryCode?: string,
      address?: string,
      addressStringManual?: string,
      coordinates?: Coordinates,
      coordinatesManual?: Coordinates,
      realEstateProperty?: RealEstatePropertyPurchaseFlowDTO
      realEstatePropertyId?: string,
    } = {}

    if (isAddressManually) {
      placeObject = {
        ...placeObject,
        addressStringManual: addressManualValue,
        coordinatesManual: { lat: markerPosition?.lat ?? 0, lng: markerPosition?.lng ?? 0 },
      }
    }

    if (selectedCountryCode) {
      placeObject = {
        ...placeObject,
        countryCode: selectedCountryCode,
      }
    }

    if (selectedAssignmentPlace) {
      placeObject = {
        ...placeObject,
        countryCode: selectedCountryCode,
        address: selectedAssignmentPlace?.formatted_address,
        coordinates: selectedAssignmentPlace.geometry.location,
      }
    }

    if (realEstateProperty) {
      // TODO: Here we will need to extend the functionality and send realEstateProperty only when it has some change to original property id
      placeObject = {
        ...placeObject,
        realEstateProperty: (realEstatePropertyId === null ? realEstateProperty : undefined),
        realEstatePropertyId: realEstatePropertyId ?? undefined,
      }
    }

    const orderComments = fullProductList.reduce((acc, product, index, array) => {
      // If product has no comment -> return
      if (!product.comment) return acc

      let comment = ''

      // Add spacing under shooting comments and subsequent product comments
      if (index > 0 || shootingComments) {
        comment += '\n\n'
      }

      // If only one product is selected, no need for prefixing
      if (array.length === 1) comment += product.comment
      // If multiple products are selected, add prefix to tie comment to it's product
      else comment += `${t(`product:p_${product.id}`)} [#${product.id}]: ${product.comment}`

      return `${acc}${comment}`
    }, shootingComments)

    const body: PlaceOrderBody = {
      role: roles,
      workspaceId: orderWorkspaceId,
      sessionId,
      data: {
        ...placeObject,
        category: selectedCategory,
        segment: selectedSegment || ProductSegment.all,
        products: quantityProductList,
        // Instructions
        comments: orderComments,
        meetingOnSite: getMeetingOnSite(),
        organizationThirdParty: getOrganizationThirdParty(),
        keyPickup: getKeyPickup(),
        // Floor Plan config
        floorPlanRequest: floorPlanConfigData ? floorPlanConfigData : undefined, // We don't want to send unnecessary data back to BE
        floorPlanCertificationRequest: floorPlanCertificationData,
        authoritiesDocumentRequest: authoritiesDocumentData,
        measurementOnSiteRequest: measurementRequestData,
        consumptionCertificateDetails: consumptionCertificateRequestData,
        // Energy Certificate
        certificateOfNeedDetails: energyCertificateRequestData,
        externalReporting: getExternalReporting(),
        // Staging
        stagingRequest: stagingConfiguration,
        // Book CT
        selectedFavouriteCreativeId,
        // AI Editing config
        aiEditingRequest: {
          skyReplacement: isSkyReplacementEnabled,
          cloudType: selectedSkyReplacement
        },
        // Other
        orderAttemptId,
        launchedFrom,
        reference,
        propertyOwner,
        skipCtAutomation: enableSkipCtAutomation,
        skipExternalProviderSending: enableSkipExternalProviderSending,
        city: getCityFromGooglePlace(selectedAssignmentPlace),
        scheduledByCT: selectedMeetingType === ProductKind.ORGANIZATION_THIRD_PARTY,
        ccEmails: Array.from(ccEmails),
        // Billing address
        billingAddressId: selectedInvoicingAddress
      },
    }

    if (roles.isAdmin && adminSelectedUserEmail) body.data.email = adminSelectedUserEmail

    const extras = {
      currency: catalogueCurrency,
      vat: vatNumber,
      discount: discountNumber,
      roles,
    }

    placeOrderMutation.mutate(
      body,
      {
        onSuccess: () => {
          // Toast first, then analytics: a throwing provider must not flip the placed order to error (ISSUE-3098)
          spawnSuccessToast(t('step_validation.order_placed.title'))

          const purchaseMeta = { ...(realEstatePropertyId && { realtyId: realEstatePropertyId }) }
          // logAnalyticsEvent already fans out to Intercom plus the always-on providers
          // (GA/Mixpanel/Firebase/Fullstory); Userpilot isn't covered by it, so track it explicitly.
          // In the Propstack embed, Intercom/Userpilot are suppressed so only the always-on providers
          // fire — keeping the conversion event alive there without console noise (ISSUE-2310).
          logAnalyticsEvent(AnalyticsEvent.ORDER_PURCHASED, purchaseMeta)
          runProvider('userpilot', AnalyticsEvent.ORDER_PURCHASED, () => Userpilot.track(AnalyticsEvent.ORDER_PURCHASED))
        },
        onError: (error) => {
          // ISSUE-3459. Not reported: an expected client error, and one Sentry issue per expired draft
          // would bury the failures that are ours.
          const missingSessionFiles = parseMissingSessionFiles(error)
          if (missingSessionFiles) {
            spawnErrorToast(t('step_validation.missing_source_files', {
              count: missingSessionFiles.files.length,
              files: missingFileNames(missingSessionFiles),
            }))
            return
          }

          spawnErrorToast(t('step_validation.order_error_text'), { error: error.request })

          const message = 'placeOrder - request state is ERROR'
          return Sentry.captureMessage(message, scope => {
            scope.clearBreadcrumbs()
            scope.setFingerprint([message])
            scope.setExtra('actionBody', body)
            scope.setExtra('fullProductList', fullProductList)
            scope.setExtra('extras', extras)
            return scope
          })
        }
      }
    )
  }, [orderIsBeingPlaced, orderHasBeenPlaced, selectedCategory, sessionId, orderAttemptId, launchedFrom, selectedAssignmentPlace, organizationInstructions, selectedInvoicingAddress, getStagingConfig, selectedExtraServices, selectedCreative, selectedMeetingType, keyPickupTravelCostProduct, getProductQuantityList, getInstructionQuantityList, getProductFullList, getInstructionFullList, hasFloorPlanCertificationProduct, getFloorPlanCertificationRequestData, hasFloorPlanProduct, getFloorPlanRequestData, hasDocumentProduct, getAuthoritiesDocumentRequestData, hasMeasurementProduct, getMeasurementRequestData, hasConsumptionCertificateProduct, getConsumptionCertificateRequestData, hasEnergyCertificateProduct, getEnergyCertificateRequestData, isAddressManually, selectedCountryCode, realEstateProperty, shootingComments, roles, selectedSegment, getMeetingOnSite, getOrganizationThirdParty, getKeyPickup, getExternalReporting, isSkyReplacementEnabled, selectedSkyReplacement, reference, propertyOwner, enableSkipCtAutomation, enableSkipExternalProviderSending, ccEmails, adminSelectedUserEmail, orderWorkspaceId, catalogueCurrency, vatNumber, discountNumber, placeOrderMutation, t, addressManualValue, markerPosition?.lat, markerPosition?.lng, realEstatePropertyId, spawnSuccessToast, spawnErrorToast])

  const onPlaceOrder = useCallback(async () => {
    // Embed consent (ISSUE-2017): "Zustimmen und bestellen" is one action — record any missing
    // consent first (outside the embed / when up to date this resolves true without a request).
    // HTTP 409 counts as recorded (concurrent duplicate); any other failure blocks the order.
    const isConsentRecorded = await ensureConsentRecorded()
    if (!isConsentRecorded) {
      spawnErrorToast(t('embed:consent.record_error'))
      return
    }

    setIsStripeConfirmed(false)
    setStripeError(null)

    if (isStripePaymentMethod) {
      setIsStripeProcessing(true)
    }

    if (!orderHasBeenPlaced) {
      handlePlaceOrder()
    }
  }, [ensureConsentRecorded, handlePlaceOrder, isStripePaymentMethod, orderHasBeenPlaced, setIsStripeConfirmed, setIsStripeProcessing, setStripeError, spawnErrorToast, t])

  return {
    orderIsBeingPlaced,
    orderHasBeenPlaced,
    orderIsComplete,
    orderPlacementHasError,
    orderPlacementForbiddenErrorMessage,
    isOrderOrStripeInProgress,
    isPlaceOrderButtonDisabled,
    placeOrderMutation,
    handlePlaceOrder,
    onPlaceOrder,
  }
})
